import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Card, Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";

const ROLES = [
  { id: "customer", label: "Customer" },
  { id: "business_owner", label: "Business owner" },
  { id: "admin", label: "Admin" },
];

const SIGNUP_METHODS = {
  email_code: "Signed up with email code",
  password: "Signed up with phone + password",
  google: "Signed up with Google",
  apple: "Signed up with Apple",
  admin: "Created by admin",
};

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = (params = {}) =>
    api
      .get("/admin/users", { params })
      .then(({ data }) => setUsers(data.data || []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.full_name}'s account? They won't be able to log in again. This can't be undone.`)) return;
    setBusyId(u.id);
    setError(null);
    setNotice(null);
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((list) => list.filter((x) => x.id !== u.id));
      setNotice(`${u.full_name} was deleted.`);
    } catch (e) {
      setError(e?.response?.data?.message || "Couldn't delete this user. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-montserrat text-2xl font-bold text-slate-900">Users</h1>
        <Button size="sm" onClick={() => setAdding(true)}>
          <Plus className="h-4 w-4" /> Add user
        </Button>
      </div>
      <Input
        placeholder="Search by name, email, phone…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && load({ q })}
        className="mb-4 w-full sm:max-w-sm"
      />

      {error && <p className="mb-4 text-sm text-destructive">{error}</p>}
      {notice && <p className="mb-4 text-sm text-emerald-700">{notice}</p>}

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-2">
          {users.map((u) => (
            <Card key={u.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div className="min-w-0">
                <p className="font-semibold text-slate-900">{u.full_name}</p>
                <p className="text-xs text-muted-foreground break-words">
                  {[u.email, u.phone].filter(Boolean).join(" · ")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {SIGNUP_METHODS[u.signup_method] || "Sign-up method not recorded"}
                  {u.created_at ? ` · ${new Date(u.created_at).toLocaleDateString()}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge>{u.role}</Badge>
                {u.id !== me?.id && (
                  <Button size="sm" variant="danger" onClick={() => deleteUser(u)} disabled={busyId === u.id}>
                    {busyId === u.id ? "Deleting…" : "Delete"}
                  </Button>
                )}
              </div>
            </Card>
          ))}
          {users.length === 0 && <p className="text-muted-foreground">No users found.</p>}
        </div>
      )}

      {adding && (
        <AddUserDialog
          onClose={() => setAdding(false)}
          onCreated={(created) => {
            setAdding(false);
            setError(null);
            setUsers((list) => [created, ...list]);
            setNotice(`${created.full_name} can now log in with ${created.email || created.phone} and the password you set.`);
          }}
        />
      )}
    </div>
  );
}

function AddUserDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "", role: "customer" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/admin/users", {
        ...form,
        email: form.email || null,
        phone: form.phone || null,
      });
      onCreated(data);
    } catch (err) {
      const errors = err?.response?.data?.errors;
      setError(
        (errors && Object.values(errors)[0]?.[0]) ||
          err?.response?.data?.message ||
          "Couldn't create the user. Please try again."
      );
      setBusy(false);
    }
  };

  const canSubmit = form.full_name.trim() && (form.email.trim() || form.phone.trim()) && form.password.length >= 8;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <form
        role="dialog"
        aria-modal="true"
        aria-label="Add user"
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-5 shadow-xl"
        onSubmit={submit}
      >
        <h2 className="font-montserrat text-lg font-bold text-slate-900">Add user</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Share the email or phone and password with them — they log in through the normal login.
        </p>

        <label htmlFor="new-user-name" className="mb-1 block text-xs text-muted-foreground">
          Full name
        </label>
        <Input id="new-user-name" value={form.full_name} onChange={set("full_name")} className="mb-3" required />

        <label htmlFor="new-user-email" className="mb-1 block text-xs text-muted-foreground">
          Email
        </label>
        <Input id="new-user-email" type="email" value={form.email} onChange={set("email")} className="mb-3" />

        <label htmlFor="new-user-phone" className="mb-1 block text-xs text-muted-foreground">
          Phone {form.email.trim() ? "(optional)" : ""}
        </label>
        <Input id="new-user-phone" type="tel" value={form.phone} onChange={set("phone")} className="mb-3" placeholder="+250…" />

        <label htmlFor="new-user-password" className="mb-1 block text-xs text-muted-foreground">
          Password (at least 8 characters)
        </label>
        <div className="mb-3">
          <Input id="new-user-password" type="password" value={form.password} onChange={set("password")} autoComplete="new-password" />
        </div>

        <label htmlFor="new-user-role" className="mb-1 block text-xs text-muted-foreground">
          Role
        </label>
        <select
          id="new-user-role"
          value={form.role}
          onChange={set("role")}
          className="mb-4 h-11 w-full rounded-xl border border-input bg-white px-3 text-sm"
        >
          {ROLES.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label}
            </option>
          ))}
        </select>

        {error && <p className="mb-3 text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy || !canSubmit}>
            {busy ? "Creating…" : "Create user"}
          </Button>
        </div>
      </form>
    </div>
  );
}
